import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import PageHeader from '../components/PageHeader'
import TableFilters from '../components/TableFilters'
import AddServiceModal from '../components/AddServiceModal'

function fmt(n) { return '₹' + Number(n).toLocaleString('en-IN') }

function billingStatus(s) {
  if (!s.next_billing_date) return { text: '—', cls: '' }
  if (s.payment_status === 'Received') return { text: 'Paid', cls: 'badge badge-green' }
  const today = new Date(); today.setHours(0,0,0,0)
  const due = new Date(s.next_billing_date + 'T00:00:00')
  const diff = Math.round((due - today) / (1000 * 60 * 60 * 24))
  if (diff > 1) return { text: `Due in ${diff}d`, cls: 'badge badge-gray' }
  if (diff === 1) return { text: 'Due tomorrow', cls: 'badge badge-amber' }
  if (diff === 0) return { text: 'Due today', cls: 'badge badge-amber' }
  return { text: `Overdue ${Math.abs(diff)}d`, cls: 'badge badge-red' }
}

function statusBadge(s) {
  const map = { Running: 'badge-green', 'Not Started': 'badge-gray', Paused: 'badge-amber', Stopped: 'badge-red' }
  return 'badge ' + (map[s] || 'badge-gray')
}

const FILTERS = [
  { key: 'search', label: 'Search', type: 'text', fields: ['client_name', 'brand_name', 'services_scope'] },
  { key: 'billing_cycle', label: 'Billing', type: 'select', options: ['Monthly', 'Quarterly', 'Half Yearly', 'Yearly'] },
]

// Archive modal — sets status on services row
function ArchiveModal({ service, onClose, onArchived }) {
  const [status, setStatus] = useState('Paused')
  const [saving, setSaving] = useState(false)

  async function doArchive() {
    setSaving(true)
    await supabase.from('services').update({ status }).eq('id', service.id)
    onArchived(service.id, status)
    setSaving(false)
    onClose()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3>Pause / Stop Project</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 16, fontSize: 13 }}>
            <strong>{service.client_name}</strong> — {service.brand_name}
            <div style={{ color: 'var(--text2)', marginTop: 4, fontSize: 12 }}>{fmt(service.value)}</div>
          </div>
          <div className="form-field">
            <label>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}>
              <option>Paused</option>
              <option>Stopped</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={doArchive} disabled={saving}>
            {saving ? 'Saving…' : status === 'Paused' ? 'Pause Project' : 'Stop Project'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Billing history modal
function BillingHistoryModal({ service, onClose }) {
  const [records, setRecords] = useState([])
  useEffect(() => {
    supabase.from('billing_records')
      .select('*').eq('project_table', 'services').eq('project_id', service.id)
      .order('billing_date', { ascending: false })
      .then(({ data }) => setRecords(data || []))
  }, [service.id])

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3>Billing History</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ padding: 0 }}>
          <div style={{ padding: '16px 24px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
            <strong>{service.client_name}</strong> — {service.brand_name}
            <div style={{ color: 'var(--text2)', marginTop: 4, fontSize: 12 }}>{fmt(service.value)} · Marketing</div>
          </div>
          {records.length > 0 ? (
            <table>
              <thead>
                <tr><th>Date</th><th>Amount</th><th>Status</th><th>Paid on</th><th>Description</th></tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id}>
                    <td>{r.billing_date}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(r.amount)}</td>
                    <td><span className={r.status === 'Paid' ? 'badge badge-green' : r.status === 'Overdue' ? 'badge badge-red' : r.status === 'Waived' ? 'badge badge-gray' : 'badge badge-amber'}>{r.status}</span></td>
                    <td style={{ color: 'var(--text2)' }}>{r.paid_date || '—'}</td>
                    <td style={{ color: 'var(--text2)', fontSize: 12 }}>{r.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>No billing records yet</div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

export default function Marketing() {
  const [services, setServices] = useState([])
  const [clientsMap, setClientsMap] = useState({})   // id → name
  const [brandsMap, setBrandsMap] = useState({})     // id → name
  const [filtered, setFiltered] = useState([])

  const [addModal, setAddModal] = useState(false)
  const [editService, setEditService] = useState(null)
  const [archiveService, setArchiveService] = useState(null)
  const [billingService, setBillingService] = useState(null)

  const handleFiltered = useCallback(d => setFiltered(d), [])

  useEffect(() => {
    async function load() {
      const [sRes, cRes, bRes] = await Promise.all([
        supabase.from('services')
          .select('*')
          .eq('service_type', 'Marketing')
          .not('status', 'in', '("Paused","Stopped")')
          .order('created_at', { ascending: false }),
        supabase.from('clients').select('id, name'),
        supabase.from('brands').select('id, name'),
      ])
      const cm = {}; (cRes.data || []).forEach(c => { cm[c.id] = c.name })
      const bm = {}; (bRes.data || []).forEach(b => { bm[b.id] = b.name })
      setClientsMap(cm)
      setBrandsMap(bm)

      const rows = (sRes.data || []).map(s => ({
        ...s,
        client_name: cm[s.client_id] || '—',
        brand_name: bm[s.brand_id] || '—',
      }))
      setServices(rows)

      // Auto-generate billing records for overdue recurring services
      const today = new Date().toISOString().slice(0, 10)
      for (const s of rows) {
        if (s.next_billing_date && s.next_billing_date <= today) {
          const { data: existing } = await supabase.from('billing_records')
            .select('id').eq('project_table', 'services').eq('project_id', s.id).eq('billing_date', s.next_billing_date).maybeSingle()
          if (!existing) {
            await supabase.from('billing_records').insert([{
              client_id: s.client_id,
              brand_id: s.brand_id,
              project_table: 'services',
              project_id: s.id,
              amount: Number(s.value) || 0,
              billing_date: s.next_billing_date,
              due_date: s.next_billing_date,
              status: 'Pending',
              description: (bm[s.brand_id] || s.brand_id) + ' — ' + s.next_billing_date,
            }]).select()
          }
        }
      }
      // Mark overdue billing records
      await supabase.from('billing_records')
        .update({ status: 'Overdue' })
        .eq('status', 'Pending')
        .lt('due_date', today)
        .eq('project_table', 'services')
    }
    load()
  }, [])

  function enrich(s) {
    return { ...s, client_name: clientsMap[s.client_id] || '—', brand_name: brandsMap[s.brand_id] || '—' }
  }

  function onSaved(service) {
    const enriched = enrich(service)
    if (editService) {
      setServices(prev => prev.map(s => s.id === service.id ? enriched : s))
    } else {
      // Refresh maps in case new client/brand was added
      Promise.all([
        supabase.from('clients').select('id, name'),
        supabase.from('brands').select('id, name'),
      ]).then(([cRes, bRes]) => {
        const cm = {}; (cRes.data || []).forEach(c => { cm[c.id] = c.name })
        const bm = {}; (bRes.data || []).forEach(b => { bm[b.id] = b.name })
        setClientsMap(cm); setBrandsMap(bm)
        setServices(prev => [...prev, { ...service, client_name: cm[service.client_id] || '—', brand_name: bm[service.brand_id] || '—' }])
      })
    }
    setAddModal(false)
    setEditService(null)
  }

  function onArchived(id) {
    setServices(prev => prev.filter(s => s.id !== id))
  }

  async function markPaid(id) {
    const svc = services.find(s => s.id === id)
    if (!svc) return
    const today = new Date().toISOString().slice(0, 10)
    await supabase.from('billing_records').insert([{
      client_id: svc.client_id,
      brand_id: svc.brand_id,
      project_table: 'services',
      project_id: id,
      amount: Number(svc.value) || 0,
      billing_date: today,
      due_date: svc.next_billing_date || today,
      status: 'Paid',
      paid_date: today,
      description: svc.brand_name + ' — Payment received',
    }]).select()
    // Advance next billing date
    let updates = { payment_status: 'Received' }
    if (svc.next_billing_date && svc.billing_day) {
      const cur = new Date(svc.next_billing_date + 'T00:00:00')
      const cycle = svc.billing_cycle || 'Monthly'
      if (cycle === 'Monthly') cur.setMonth(cur.getMonth() + 1)
      else if (cycle === 'Quarterly') cur.setMonth(cur.getMonth() + 3)
      else if (cycle === 'Half Yearly') cur.setMonth(cur.getMonth() + 6)
      else cur.setFullYear(cur.getFullYear() + 1)
      updates = { payment_status: 'Pending', next_billing_date: cur.toISOString().slice(0, 10) }
    }
    await supabase.from('services').update(updates).eq('id', id)
    setServices(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s))
  }

  async function remove(id) {
    if (!window.confirm('Delete this project?')) return
    await supabase.from('services').delete().eq('id', id)
    setServices(prev => prev.filter(s => s.id !== id))
  }

  const monthlyValue = services.reduce((sum, s) => sum + (Number(s.value) || 0), 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PageHeader
        title="Active Projects — Marketing"
        subtitle={`${services.length} projects · ${fmt(monthlyValue)} total`}
        action={<button className="btn btn-primary" onClick={() => setAddModal(true)}>+ Add project</button>}
      />
      <div style={{ padding: 28, flex: 1, overflowX: 'auto' }}>
        <TableFilters filters={FILTERS} data={services} onFiltered={handleFiltered} />
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 1000 }}>
            <thead>
              <tr>
                <th>Client</th>
                <th>Brand / Project</th>
                <th>Services</th>
                <th>Value</th>
                <th>Billing period</th>
                <th>Billing day</th>
                <th>Next billing</th>
                <th>Billing status</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Assigned</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const bs = billingStatus(s)
                return (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 500 }}>{s.client_name}</td>
                    <td>
                      <div>{s.brand_name}</div>
                      {s.remarks && <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 2 }}>{s.remarks}</div>}
                    </td>
                    <td style={{ color: 'var(--text2)', fontSize: 12 }}>{s.services_scope || '—'}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{fmt(s.value)}</td>
                    <td><span className="badge badge-gray">{s.billing_cycle || '—'}</span></td>
                    <td style={{ color: 'var(--text2)' }}>
                      {s.billing_day ? s.billing_day + (s.billing_day===1?'st':s.billing_day===2?'nd':s.billing_day===3?'rd':'th') : '—'}
                    </td>
                    <td style={{ color: 'var(--text2)' }}>{s.next_billing_date || '—'}</td>
                    <td>
                      {bs.cls
                        ? <span className={bs.cls}>{bs.text}</span>
                        : <span style={{ color: 'var(--text3)' }}>{bs.text}</span>}
                    </td>
                    <td>
                      {s.payment_status === 'Received'
                        ? <span className="badge badge-green">Paid</span>
                        : <button className="badge badge-amber" style={{ cursor: 'pointer', border: 'none' }} onClick={() => markPaid(s.id)}>Mark Paid</button>
                      }
                    </td>
                    <td><span className={statusBadge(s.status)}>{s.status}</span></td>
                    <td style={{ color: 'var(--text2)' }}>{s.assigned_to || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setEditService(s)}>Edit</button>
                        <button className="btn" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => setBillingService(s)}>Bills</button>
                        <button className="btn" style={{ padding: '4px 10px', fontSize: 12, color: 'var(--warning)', borderColor: 'var(--warning)' }} onClick={() => setArchiveService(s)}>Pause</button>
                        <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => remove(s.id)}>Del</button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>No marketing projects yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {addModal && (
        <AddServiceModal
          lockedServiceType="Marketing"
          onClose={() => setAddModal(false)}
          onSaved={onSaved}
        />
      )}

      {editService && (
        <AddServiceModal
          lockedServiceType="Marketing"
          editService={editService}
          onClose={() => setEditService(null)}
          onSaved={onSaved}
        />
      )}

      {archiveService && (
        <ArchiveModal
          service={archiveService}
          onClose={() => setArchiveService(null)}
          onArchived={onArchived}
        />
      )}

      {billingService && (
        <BillingHistoryModal
          service={billingService}
          onClose={() => setBillingService(null)}
        />
      )}
    </div>
  )
}
